import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { getOutsideRentalProfile } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { billingSummary } from '../../utils/rentalBilling';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const statusColor = (status) => {
  const map = { Active: 'success', Overdue: 'error', Completed: 'info', Cancelled: 'default' };
  return map[status] || 'default';
};

const RentalOutsideProfile = () => {
  const { serial } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getOutsideRentalProfile(decodeURIComponent(serial || ''));
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load outside rent profile'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [serial]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <CircularProgress />;
  if (error && !data) return <Alert severity="error">{error}</Alert>;

  const p = data;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/rentals/outside')} sx={{ mb: 2 }}>
        All outside rent
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Typography variant="h5" fontWeight={700}>{p.product_name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Serial {p.serial_number || '—'}
        {p.purchase_date ? ` · Purchased ${p.purchase_date}` : ''}
        {p.components ? ` · ${p.components}` : ''}
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Paid so far', value: money(p.paid) },
          { label: 'Will pay (total)', value: money(p.total) },
          { label: 'Still to pay', value: money(p.remaining) },
          { label: 'Purchase price', value: money(p.purchase_price) },
          { label: 'Asset value', value: money(p.asset_value) },
          { label: 'Customer billed', value: money(p.customer_billed) }
        ].map((card) => (
          <Grid item xs={6} md={2} key={card.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="subtitle1" fontWeight={700}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography fontWeight={700} gutterBottom>Rentals for this item</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rental</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Outside term</TableCell>
              <TableCell align="right">Paid so far</TableCell>
              <TableCell align="right">Will pay</TableCell>
              <TableCell align="right">Customer price</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(p.rentals || []).map((r) => (
              <TableRow key={r.rental_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/${r.rental_id}`)}>
                <TableCell>{r.rental_number}</TableCell>
                <TableCell>{r.customer?.name || '—'}</TableCell>
                <TableCell>{r.start_date} → {r.end_date}</TableCell>
                <TableCell>{billingSummary(r.outside_cycle || r.cycle, r.outside_duration || r.periods)}</TableCell>
                <TableCell align="right">{money(r.paid)}</TableCell>
                <TableCell align="right">{money(r.total)}</TableCell>
                <TableCell align="right">{money(r.customer_price)}</TableCell>
                <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default RentalOutsideProfile;
